import { SessionUser, UserRoles } from '@mangovoice/types'

/**
 * Extracts user information from a JWT token
 * @param token JWT token string
 * @returns SessionUser object or undefined if token is invalid/missing
 */
export const getUserFromToken = (token?: string): SessionUser | undefined => {
  if (!token) {
    return undefined
  }

  try {
    const tokenBody = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const tokenBodyDecoded = atob(tokenBody)
    const parsed = JSON.parse(tokenBodyDecoded)

    const {
      email,
      id: regularId,
      username: regularUsername,
      first_name: regularFirstName,
      last_name: regularLastName,
      ['custom:user_id']: customUserId,
      ['custom:first_name']: customFirstName,
      ['custom:last_name']: customLastName,
      ['cognito:username']: cognitoUsername,
    } = parsed

    // Cognito provides claims with either "custom:" or "cognito:" on them
    // This defaults to the cognito way, and falls back to the simpleJWT way
    // to ensure all profile data is proper.
    const first_name = customFirstName ?? regularFirstName
    const last_name = customLastName ?? regularLastName
    const username = cognitoUsername ?? regularUsername
    const id = customUserId ?? regularId

    return {
      id,
      username,
      first_name,
      last_name,
      email,
    }
  } catch (err) {
    console.error('Failed to parse JWT token:', err)
    return undefined
  }
}

export const isUserAdmin = (userRoles?: UserRoles): boolean => {
  if (!userRoles) return false

  const adminRoleNames = ['mango', 'super_user', 'admin']
  return (
    userRoles.is_superuser ||
    userRoles.roles?.some((role) =>
      adminRoleNames.some((adminRole) => role.name.includes(adminRole)),
    )
  )
}

export type PortalUserLevel = 'limited' | 'restricted' | 'unrestricted'
export type AdministratorUserLevel = 'admin' | 'senior' | 'super'
/** Union of all account access levels accepted by role-checking helpers. */
export type AccountUserLevel = PortalUserLevel | AdministratorUserLevel
/** Function signature for role checks (typically returned by useCheckRole). */
export type CheckRoleFn = (requiredRole: AccountUserLevel) => boolean

export interface UserRoleClassification {
  administratorLevel: AdministratorUserLevel | null
  portalLevel: PortalUserLevel | null
  isMangoStaff: boolean
  isSuperuser: boolean
  roleNames: string[]
  roleTypes: string[]
}

/**
 * Classifies a user's role levels for portal/admin UX decisions.
 * This is additive and can be used alongside legacy role checks.
 */
export const getUserRoleClassification = (
  userRoles?: UserRoles,
): UserRoleClassification => {
  if (!userRoles) {
    return {
      administratorLevel: null,
      portalLevel: null,
      isMangoStaff: false,
      isSuperuser: false,
      roleNames: [],
      roleTypes: [],
    }
  }

  const roleNames = (userRoles.roles ?? []).map((role) => role.name)
  const roleTypes = (userRoles.roles ?? []).map((role) => role.type)

  const normalizedRoleNames = roleNames.map((name) => name.toLowerCase().trim())
  const normalizedRoleTypes = roleTypes.map((type) => type.toLowerCase().trim())

  const hasRoleName = (name: string) => normalizedRoleNames.includes(name)
  const hasRoleType = (fragment: string) =>
    normalizedRoleTypes.some((type) => type.includes(fragment))

  let administratorLevel: AdministratorUserLevel | null = null
  if (
    userRoles.is_superuser ||
    hasRoleName('super_admin') ||
    hasRoleName('super_user') ||
    hasRoleType('super administrator')
  ) {
    administratorLevel = 'super'
  } else if (
    hasRoleName('mango') ||
    hasRoleType('internal administrator') ||
    hasRoleType('senior administrator')
  ) {
    administratorLevel = 'senior'
  } else if (hasRoleName('admin') || hasRoleType('administrator')) {
    administratorLevel = 'admin'
  }

  let portalLevel: PortalUserLevel | null = null
  if (hasRoleType('portal unrestricted')) {
    portalLevel = 'unrestricted'
  } else if (hasRoleType('portal restricted')) {
    portalLevel = 'restricted'
  } else if (hasRoleType('portal limited')) {
    portalLevel = 'limited'
  }

  return {
    administratorLevel,
    portalLevel,
    isMangoStaff: userRoles.is_staff || hasRoleName('mango'),
    isSuperuser: userRoles.is_superuser,
    roleNames,
    roleTypes,
  }
}

const ADMINISTRATOR_LEVEL_RANK: Record<AdministratorUserLevel, number> = {
  admin: 0,
  senior: 1,
  super: 2,
}

const PORTAL_LEVEL_RANK: Record<PortalUserLevel, number> = {
  limited: 0,
  restricted: 1,
  unrestricted: 2,
}

const isAdministratorUserLevel = (
  role: AccountUserLevel,
): role is AdministratorUserLevel => {
  return role === 'admin' || role === 'senior' || role === 'super'
}

const isPortalUserLevel = (role: AccountUserLevel): role is PortalUserLevel => {
  return role === 'limited' || role === 'restricted' || role === 'unrestricted'
}

/**
 * Generic role checker for account access levels.
 *
 * Rules:
 * - Mango staff and superusers always return true.
 * - Admin role checks compare within administrator hierarchy: admin < senior < super.
 * - Portal role checks compare within portal hierarchy: limited < restricted < unrestricted.
 * - Any administrator level satisfies portal-level checks.
 *
 * @example
 * const canAccessAdminFeatures = checkRole(userRoles, 'admin')
 * const canDelete = checkRole(userRoles, 'senior')
 * const canAccessUnrestrictedPortal = checkRole(userRoles, 'unrestricted')
 *
 * @remarks
 * https://support.mangovoice.com/user-roles-and-permissions/
 */
export const checkRole = (
  userRoles: UserRoles | undefined,
  requiredRole: AccountUserLevel,
): boolean => {
  const roleClassification = getUserRoleClassification(userRoles)

  if (roleClassification.isSuperuser || roleClassification.isMangoStaff) {
    return true
  }

  if (isAdministratorUserLevel(requiredRole)) {
    if (!roleClassification.administratorLevel) return false

    return (
      ADMINISTRATOR_LEVEL_RANK[roleClassification.administratorLevel] >=
      ADMINISTRATOR_LEVEL_RANK[requiredRole]
    )
  }

  if (isPortalUserLevel(requiredRole)) {
    // Administrators satisfy all portal-level checks.
    if (roleClassification.administratorLevel) return true
    if (!roleClassification.portalLevel) return false

    return (
      PORTAL_LEVEL_RANK[roleClassification.portalLevel] >=
      PORTAL_LEVEL_RANK[requiredRole]
    )
  }

  return false
}

/**
 * Checks if user has superuser permissions.
 * Used for privileged features (e.g. AI Agent node in switchboard).
 */
export const isUserSuperuser = (userRoles?: UserRoles): boolean => {
  return userRoles?.is_superuser ?? false
}

export const isMangoStaffOrSuperuser = (userRoles?: UserRoles): boolean => {
  return Boolean(userRoles?.is_staff || userRoles?.is_superuser)
}
